import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const Partners = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Partner with Seoro - Agency & Platform Partnerships"
        description="Join Seoro's partner program. Offer Seoro to your clients, integrate into your platform, or build complementary tools. Special partner pricing, dedicated support, and revenue share opportunities available."
        keywords="Seoro partners, SEO agency partnership, platform integration, technology partners, partner program, revenue share"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Partner with Seoro
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Join our partner program and help businesses grow their organic traffic.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Why Partner with Seoro?</h2>
              <p className="text-muted-foreground leading-relaxed">
                We're building the future of SEO content automation, and we want to work with partners 
                who share our vision. Whether you're helping businesses grow their organic traffic or 
                building tools that complement ours, there's a place for you in our partner program.
              </p>
            </section>

            <section className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">Partnership Types</h2>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-colors">
                  <h3 className="text-xl font-bold text-foreground mb-3">Agency Partners</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Offer Seoro to your clients as part of your SEO services. We provide special 
                    agency pricing and white-label options so you can deliver results while building 
                    your brand. Perfect for SEO agencies, marketing firms, and consultants who want 
                    to scale their content services.
                  </p>
                </div>
                <div className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-colors">
                  <h3 className="text-xl font-bold text-foreground mb-3">Platform Partners</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Integrate Seoro into your platform, marketplace, or SaaS product. We offer API 
                    access and custom integration support to help you provide value to your users. 
                    Ideal for platforms that serve businesses looking to grow their online presence.
                  </p>
                </div>
                <div className="bg-muted/30 rounded-xl p-6 hover:bg-muted/50 transition-colors">
                  <h3 className="text-xl font-bold text-foreground mb-3">Technology Partners</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Build integrations or complementary tools that work with Seoro. We provide 
                    developer resources, documentation, and support to help you create solutions 
                    that enhance the Seoro ecosystem. Great for developers and tech companies 
                    building SEO or content tools.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">What You Get</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Special partner pricing and revenue share opportunities</li>
                <li>Dedicated partner support and resources</li>
                <li>Marketing materials and co-branding options</li>
                <li>Early access to new features and updates</li>
                <li>Technical support for integrations</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-foreground">Get Started</h2>
              <p className="text-muted-foreground leading-relaxed">
                Interested in becoming a partner? We'd love to hear from you. Reach out to us at{" "}
                <a href="mailto:partners@seoro.so" className="text-primary hover:underline font-semibold">
                  partners@seoro.so
                </a>{" "}
                and tell us about your business and how you'd like to work together. We'll get back 
                to you within 48 hours to discuss partnership opportunities.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Partners;


